import { Box } from "@mui/material";
import React, { useState } from "react";
import { HeaderText } from "../../shared/text/HeaderText.styled";
import FullScreenDrawerLayout from "../../shared/layout/FullScreenDrawerLayout";
import CardActionButton from "../../shared/buttons/CardActionButton";
import { useDrawer } from "../../context/DrawerContext";
import { useBanner } from "../../context/BannerContext";
import { useChartActions } from "../../hooks/useChartActions";
import { EdiphiChartType } from "../../../excel/charts/api";
import { ediphiChartTypes } from "../../../excel/charts/api/registry";
import { track } from "../../../clients/mixpanel";
import { SCROLLBAR_WIDTH, thinScrollbarSx } from "../../styles/scrollbar";
import { CHARTS_LOAD_ERROR, chartCreateError } from "../../constants";
import ChartIconImage from "./ChartIconImage";

interface ChartsDrawerProps {
  /** Rediscovers the workbook's charts, so a newly created one gets a card without a reload. */
  reloadCharts: () => Promise<void>;
}

const ChartsDrawer = ({ reloadCharts }: ChartsDrawerProps) => {
  const { isOpen, close } = useDrawer("insights");
  const { showBanner } = useBanner();
  const { withChartLogging } = useChartActions();

  /** Which chart is being built, so its button alone shows the spinner. */
  const [pendingType, setPendingType] = useState<string | null>(null);

  const handleCreateChart = async (chartType: EdiphiChartType) => {
    setPendingType(chartType.key);
    const startTime = performance.now();
    let succeeded = false;
    try {
      await withChartLogging(async () => chartType.chartBuilder({}), {
        context: "ChartsDrawer_handleCreateChart",
        chartType: chartType.key,
        message: `Error creating ${chartType.title} chart`,
      });
      track("excel.chart.create", { chartType: chartType.key, loadTime: (performance.now() - startTime) / 1000 });
      succeeded = true;
    } catch {
      showBanner(chartCreateError(chartType.title), "error");
    } finally {
      // Unconditional: a run that failed part-way can still have left a chart behind.
      setPendingType(null);
      await reloadCharts().catch(() => showBanner(CHARTS_LOAD_ERROR, "error"));
      // Only on success: a failure leaves the drawer up so the retry is one click away, matching
      // every other add path. Rediscovery above still surfaces cards for whatever landed.
      if (succeeded) close();
    }
  };

  const generateDrawerTitle = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box component="img" src="assets/ediphi_logo.svg" alt="" aria-hidden sx={{ width: "16px", height: "16px" }} />
      <HeaderText>Add chart</HeaderText>
    </Box>
  );

  return (
    <FullScreenDrawerLayout isDrawerOpen={isOpen} onDrawerClose={close} drawerTitle={generateDrawerTitle()}>
      <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            p: 2,
            // The reserved track sits inside padding-right, so trim it by the track's width to keep
            // both gutters at 16px.
            pr: `${16 - SCROLLBAR_WIDTH}px`,
            flex: 1,
            minHeight: 0,
            // `scroll`, not `auto` — the trimmed padding above assumes the track always exists.
            overflowY: "scroll",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            ...thinScrollbarSx,
          }}
        >
          {ediphiChartTypes.map((chartType) => (
            <CardActionButton
              key={chartType.key}
              // No argument, so each type shows its default shape rather than a live chart's.
              leadingIcon={<ChartIconImage icon={chartType.chartIconGetter()} />}
              label={chartType.title}
              onClick={() => void handleCreateChart(chartType)}
              loading={pendingType === chartType.key}
              disabled={pendingType !== null}
            />
          ))}
        </Box>
      </Box>
    </FullScreenDrawerLayout>
  );
};

export default ChartsDrawer;
